// src/components/pages/message/MessagesPage.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CheckIcon from "@mui/icons-material/Check";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ListSubheader from "@mui/material/ListSubheader";
import Countdown from "react-countdown";

import { db, auth, storage } from "../../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";
import { alpha } from "@mui/material/styles";
import {messageToBeOppendText,formatDuration} from "../../../utils/commonFunctions"
import { getProfilePathByUid } from "../../../utils/publicProfile";
import { ellipsizeProfileText } from "../../../utils/profileText";
import { isSelfOnlyPrivateCapsule } from "../../../utils/messageClassification";
import {
  useReceivedMessages,
  useSentMessages,
  useSelfPrivateMessages,
  useArchivedMessages,
  useDeletedMessages,
} from "../../../hooks/useMessageLists";
import {
  formatHumanDateTime,
  formatCompactTime,
  SWIPE_ACTION_META,
  PRIMARY_TABS,
  READ_FILTERS,
  SORT_OPTIONS,
  getSortShortLabel,
  sortMessages,
  isDraftCapsuleMessage,
  isSponsoredCapsuleMessage,
  getSwipeDialogConfig,
} from "./messageListHelpers";

const MessageEmptyState = React.memo(function MessageEmptyState({
  hasSearch,
  activeViewLabel,
}) {
  return (
    <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {hasSearch ? "No matching messages" : `No ${activeViewLabel.toLowerCase()} messages yet`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {hasSearch
          ? "Try a different keyword or clear search."
          : "New activity will appear here as you send and receive time capsules."}
      </Typography>
    </Box>
  );
});

const MessageListSkeleton = React.memo(function MessageListSkeleton() {
  return (
    <Box sx={{ px: 0.7 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", px: 1.2, py: 0.8, fontWeight: 600 }}
      >
        Loading messages...
      </Typography>
      {Array.from({ length: 7 }).map((_, index) => (
        <Box
          key={`message-skeleton-${index}`}
          sx={{
            px: 1.2,
            py: 1.1,
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 0.9 }}>
            <Skeleton variant="circular" width={36} height={36} animation="wave" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width="42%" height={14} animation="wave" />
              <Skeleton variant="text" width="28%" height={12} animation="wave" />
            </Box>
          </Box>
          <Skeleton variant="rounded" width="52%" height={20} animation="wave" />
          <Skeleton variant="text" width="36%" height={14} animation="wave" sx={{ mt: 0.5 }} />
          <Skeleton variant="text" width="90%" height={14} animation="wave" sx={{ mt: 0.8 }} />
        </Box>
      ))}
    </Box>
  );
});

const MessagePrimarySegmentedTabs = React.memo(function MessagePrimarySegmentedTabs({
  tabValue,
  onChangeTab,
}) {
  const normalizedTabValue = Number.isFinite(tabValue) ? tabValue : 0;

  return (
    <Tabs
      value={normalizedTabValue}
      onChange={(_, nextValue) => onChangeTab(nextValue)}
      variant="fullWidth"
      sx={{
        flex: 1,
        minHeight: 44,
        "& .MuiTab-root": {
          textTransform: "none",
          fontWeight: 700,
          minHeight: 44,
          fontSize: { xs: "0.83rem", sm: "0.9rem" },
        },
      }}
    >
      {PRIMARY_TABS.map((tab) => (
        <Tab key={tab.value} value={tab.value} label={tab.label} />
      ))}
    </Tabs>
  );
});

const MessageHeader = React.memo(function MessageHeader({
  tabValue,
  onChangeTab,
  showSentFilters,
  sentSponsoredCount,
  sentSponsoredOnly,
  isBusinessAccount,
  onToggleSponsored,
  searchValue,
  onSearchChange,
  secondaryView,
  onClearSecondaryView,
  onOpenFoldersMenu,
  activeView,
  readFilter,
  onSelectReadFilter,
  unreadReceivedCount,
  sortShortLabel,
  onOpenSortMenu,
  fromSenderName,
  onClearFromSender,
}) {
  const secondaryViewLabel = secondaryView === "archived"
    ? "Viewing Archived"
    : secondaryView === "deleted"
      ? "Viewing Deleted"
      : secondaryView === "drafts"
        ? "Viewing Drafts"
      : "";
  const showReadFilters = activeView === "received";

  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        pt: 1,
        pb: 0.75,
        backgroundColor: "background.default",
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 6px 14px rgba(0,0,0,0.18)"
            : "0 6px 14px rgba(25,58,138,0.06)",
        overflow: "visible",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, pb: 0.8, minHeight: 46 }}>
        <MessagePrimarySegmentedTabs tabValue={tabValue} onChangeTab={onChangeTab} />
        <Button
          onClick={onOpenFoldersMenu}
          aria-label="Open folders"
          startIcon={<FolderOutlinedIcon />}
          sx={(theme) => ({
            minHeight: 46,
            pl: 2,
            pr: 2,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 800,
            fontSize: "0.83rem",
            whiteSpace: "nowrap",
            color: theme.colors.blue,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.04)
                : alpha(theme.colors.blue, 0.06),
            flex: "0 0 auto",
            "& .MuiButton-startIcon": { ml: 0, mr: 0.9 },
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.08)
                  : alpha(theme.colors.blue, 0.12),
            },
          })}
        >
          Folders
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 0.2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search people, places, memories"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ opacity: 0.75 }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search messages" }}
          sx={(theme) => ({
            my: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.common.white, 0.92),
            },
          })}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0, rowGap: 0.8 }}>
            {showReadFilters &&
              READ_FILTERS.map((filter) => {
                const selected = readFilter === filter.value;
                const count = filter.value === "unread" ? unreadReceivedCount : null;
                return (
                  <Chip
                    key={filter.value}
                    size="small"
                    clickable
                    onClick={() => onSelectReadFilter(filter.value)}
                    label={count ? `${filter.label} ${count}` : filter.label}
                    sx={(theme) => ({
                      height: 32,
                      borderRadius: "999px",
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 1.8 },
                      backgroundColor: selected
                        ? theme.colors.blue
                        : theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.07)
                          : alpha(theme.colors.blue, 0.06),
                      color: selected
                        ? theme.palette.common.white
                        : theme.palette.mode === "dark"
                          ? theme.palette.text.primary
                          : theme.colors.blue,
                      "&:hover": {
                        backgroundColor: selected
                          ? theme.colors.blue
                          : theme.palette.mode === "dark"
                            ? alpha(theme.palette.text.primary, 0.12)
                            : alpha(theme.colors.blue, 0.12),
                      },
                    })}
                  />
                );
              })}
          </Box>
          <Button
            onClick={onOpenSortMenu}
            aria-label="Sort and filter messages"
            startIcon={<SwapVertIcon />}
            sx={(theme) => ({
              flex: "0 0 auto",
              minHeight: 32,
              pl: 1.6,
              pr: 1.6,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8rem",
              whiteSpace: "nowrap",
              color:
                theme.palette.mode === "dark" ? theme.palette.text.primary : theme.colors.blue,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.04)
                  : alpha(theme.colors.blue, 0.06),
              "& .MuiButton-startIcon": { ml: 0, mr: 0.7 },
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.primary, 0.08)
                    : alpha(theme.colors.blue, 0.12),
              },
            })}
          >
            {sortShortLabel}
          </Button>
        </Box>

        {showReadFilters && fromSenderName && (
          <Box sx={{ display: "flex" }}>
            <Chip
              size="small"
              label={`From: ${fromSenderName}`}
              onDelete={onClearFromSender}
              deleteIcon={<CloseIcon />}
              sx={{ height: 28, borderRadius: "999px", fontWeight: 700 }}
            />
          </Box>
        )}
        {(secondaryViewLabel || (showSentFilters && isBusinessAccount)) && (
          <>
            <Divider />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {secondaryViewLabel && (
                <Chip
                  label={secondaryViewLabel}
                  onDelete={onClearSecondaryView}
                  deleteIcon={<CloseIcon />}
                  sx={(theme) => ({
                    height: 30,
                    borderRadius: "999px",
                    fontWeight: 700,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.primary, 0.08)
                        : alpha(theme.colors.blue, 0.08),
                  })}
                />
              )}
              {showSentFilters && isBusinessAccount && (
                <Chip
                  size="small"
                  clickable
                  label={`Sponsored (${sentSponsoredCount})`}
                  onClick={onToggleSponsored}
                  sx={(theme) => ({
                    borderRadius: "999px",
                    fontWeight: 700,
                    height: 30,
                    px: 0.25,
                    border: "1px solid",
                    borderColor: sentSponsoredOnly
                      ? theme.colors.blue
                      : theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.secondary, 0.35)
                        : alpha(theme.colors.blue, 0.28),
                    backgroundColor: sentSponsoredOnly
                      ? theme.colors.blue
                      : theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.primary, 0.07)
                        : alpha(theme.colors.blue, 0.06),
                    color: sentSponsoredOnly
                      ? theme.palette.common.white
                      : theme.palette.mode === "dark"
                        ? theme.palette.text.primary
                        : theme.colors.blue,
                    "&:hover": {
                      backgroundColor: sentSponsoredOnly
                        ? theme.colors.blue
                        : theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.12)
                          : alpha(theme.colors.blue, 0.12),
                    },
                  })}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
});

const MessageRowItem = React.memo(function MessageRowItem({
  msg,
  type,
  onOpenMessage,
  onOpenProfile,
  onSwipeAction,
  countdownRenderer,
  revealDate,
  isFuture,
  sentAt,
  editedAt,
  timeLabel,
  isDraftMessage,
  isSponsoredSent,
  isSelfSent,
  showAvatar,
  locationLabel,
  showUnreadDot,
  positiveSwipeAction,
  negativeSwipeAction,
}) {
  const startXRef = useRef(null);
  const dragXRef = useRef(0);
  const swipedRef = useRef(false);
  const [dragX, setDragX] = useState(0);
  const SWIPE_LIMIT = 132;
  const SWIPE_TRIGGER = 88;

  const resetSwipe = useCallback(() => {
    startXRef.current = null;
    dragXRef.current = 0;
    setDragX(0);
    window.setTimeout(() => {
      swipedRef.current = false;
    }, 0);
  }, []);

  const handleTouchStart = useCallback((event) => {
    startXRef.current = event.touches?.[0]?.clientX ?? null;
    swipedRef.current = false;
  }, []);

  const handleTouchMove = useCallback((event) => {
    if (startXRef.current === null) {
      return;
    }

    const nextX = event.touches?.[0]?.clientX;
    if (typeof nextX !== "number") {
      return;
    }

    let delta = Math.max(
      -SWIPE_LIMIT,
      Math.min(SWIPE_LIMIT, nextX - startXRef.current)
    );
    if (delta > 0 && !positiveSwipeAction) {
      delta = 0;
    }
    if (delta < 0 && !negativeSwipeAction) {
      delta = 0;
    }
    dragXRef.current = delta;
    setDragX(delta);
  }, [negativeSwipeAction, positiveSwipeAction]);

  const handleTouchEnd = useCallback(() => {
    const finalDelta = dragXRef.current;
    const swipeActionType = finalDelta > 0 ? positiveSwipeAction : negativeSwipeAction;

    if (swipeActionType && Math.abs(finalDelta) >= SWIPE_TRIGGER) {
      swipedRef.current = true;
      onSwipeAction(msg, swipeActionType);
    }

    resetSwipe();
  }, [msg, negativeSwipeAction, onSwipeAction, positiveSwipeAction, resetSwipe]);

  const handleRowClick = useCallback(() => {
    if (swipedRef.current || Math.abs(dragXRef.current) > 8) {
      return;
    }
    onOpenMessage();
  }, [onOpenMessage]);

  let swipeAction = dragX > 0
    ? SWIPE_ACTION_META[positiveSwipeAction]
    : dragX < 0
      ? SWIPE_ACTION_META[negativeSwipeAction]
      : null;
  if (swipeAction && negativeSwipeAction === "delete" && dragX < 0 && isDraftMessage) {
    swipeAction = SWIPE_ACTION_META.discard;
  }
  const senderHandleText = msg.senderPublicHandle
    ? `@${ellipsizeProfileText(String(msg.senderPublicHandle).replace(/^@+/, ""))}`
    : "";
  const senderHeader = showAvatar ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.9,
        minWidth: 0,
        mb: 0.35,
      }}
    >
      <Avatar
        src={msg.senderAvatar}
        alt={msg.senderDisplayName ? `${msg.senderDisplayName} avatar` : "Avatar"}
        role={msg.senderId ? "button" : undefined}
        tabIndex={msg.senderId ? 0 : undefined}
        aria-label={msg.senderId ? `Open ${msg.senderDisplayName || "sender"} profile` : undefined}
        sx={{ width: 36, height: 36, cursor: msg.senderId ? "pointer" : "default", flex: "0 0 auto" }}
        onClick={(event) => {
          event.stopPropagation();
          onOpenProfile();
        }}
        onKeyDown={
          msg.senderId
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenProfile();
                }
              }
            : undefined
        }
      />
      <Box sx={{ minWidth: 0, overflow: "visible", flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.primary",
            fontWeight: 700,
            lineHeight: 1.2,
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            wordBreak: "break-word",
            cursor: msg.senderId ? "pointer" : "default",
          }}
          onClick={(event) => {
            event.stopPropagation();
            onOpenProfile();
          }}
        >
          {ellipsizeProfileText(msg.senderDisplayName || "Profile")}
        </Typography>
        {senderHandleText && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              lineHeight: 1.15,
              whiteSpace: "normal",
              overflow: "visible",
              textOverflow: "clip",
              wordBreak: "break-word",
              cursor: msg.senderId ? "pointer" : "default",
            }}
            onClick={(event) => {
              event.stopPropagation();
              onOpenProfile();
            }}
          >
            {senderHandleText}
          </Typography>
        )}
      </Box>
      {(showUnreadDot || timeLabel) && (
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flex: "0 0 auto",
            pl: 0.5,
          }}
        >
          {showUnreadDot && (
            <Box
              sx={(theme) => ({
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: theme.colors.blue,
                flex: "0 0 auto",
              })}
            />
          )}
          {timeLabel && (
            <Typography
              variant="caption"
              sx={(theme) => ({
                fontSize: "0.7rem",
                fontWeight: showUnreadDot ? 700 : 500,
                whiteSpace: "nowrap",
                color: showUnreadDot ? theme.colors.blue : theme.palette.text.secondary,
              })}
            >
              {timeLabel}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
        touchAction: "pan-y",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: swipeAction?.justify || "flex-start",
          px: 2.75,
          backgroundColor: swipeAction?.color || "transparent",
          color: "common.white",
          fontWeight: 700,
          opacity: swipeAction ? 1 : 0,
          pointerEvents: "none",
        }}
      >
        {swipeAction ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: swipeAction.flexDirection,
              gap: 0.85,
              fontSize: "0.95rem",
            }}
          >
            <swipeAction.Icon fontSize="small" />
            <Box component="span">{swipeAction.label}</Box>
          </Box>
        ) : null}
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Open message${msg.subject ? `: ${msg.subject}` : ""}`}
        onClick={handleRowClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleRowClick();
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={resetSwipe}
        sx={{
          width: "100%",
          display: "flex",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.background.paper,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          cursor: "pointer",
          minHeight: 84,
          px: 0.7,
          transform: `translateX(${dragX}px)`,
          transition: startXRef.current === null ? "transform 160ms ease" : "none",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 0.6,
            py: 0.75,
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            {isFuture && type !== "sent" ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {senderHeader}
                <Countdown date={revealDate} renderer={countdownRenderer} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 0.2,
                  py: 0.15,
                  position: "relative",
                  minWidth: 0,
                }}
              >
                {senderHeader}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.4, minWidth: 0 }}>
                  {(isDraftMessage || isSponsoredSent || isSelfSent) && (
                    <Chip
                      size="small"
                      label={
                        isDraftMessage
                          ? "Draft"
                          : isSponsoredSent
                            ? "Sponsored"
                            : "Memory"
                      }
                      color={isDraftMessage ? "warning" : "default"}
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        borderRadius: "999px",
                        flex: "0 0 auto",
                        ...(isSponsoredSent || isSelfSent
                          ? {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.text.primary, 0.08)
                                  : alpha(theme.palette.text.primary, 0.06),
                            }
                          : null),
                      }}
                    />
                  )}
                  {!showAvatar && showUnreadDot && (
                    <Box
                      sx={(theme) => ({
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: theme.colors.blue,
                        flex: "0 0 auto",
                      })}
                    />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 1,
                      textOverflow: "ellipsis",
                      fontSize: "0.95rem",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      minWidth: 0,
                    }}
                    variant="subtitle2"
                  >
                    {msg.subject}
                  </Typography>
                </Box>
                {!showAvatar && (isDraftMessage ? editedAt : sentAt) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.68rem", lineHeight: 1.2, mt: 0.2 }}
                  >
                    {isDraftMessage ? `Edited ${editedAt}` : sentAt}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.35,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 1,
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.message || "No message"}
                </Typography>

                {locationLabel && (
                  <Chip
                    size="small"
                    icon={<LocationOnOutlinedIcon />}
                    label={locationLabel}
                    sx={(theme) => ({
                      mt: 0.5,
                      height: 24,
                      maxWidth: "100%",
                      borderRadius: "999px",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      alignSelf: "flex-start",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.07)
                          : alpha(theme.colors.blue, 0.07),
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.text.secondary
                          : theme.colors.blue,
                      "& .MuiChip-icon": {
                        fontSize: "0.95rem",
                        color: "inherit",
                        ml: 0.5,
                      },
                    })}
                  />
                )}
                {isFuture && type === "sent" && (
                  <Box sx={{ flex: 1, mt: 0.25 }}>
                    <Countdown date={revealDate} renderer={countdownRenderer} />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

const MessagesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [uid, setUid] = useState(() => auth.currentUser?.uid || "");
  const [authResolved, setAuthResolved] = useState(false);
  const initialTab = parseInt(searchParams.get("tab") || "0", 10);
  const initialDraftsOnly = searchParams.get("drafts") === "1";
  const initialSearch = searchParams.get("q") || "";
  const initialSecondaryViewParam = searchParams.get("view");
  const initialSecondaryView = ["archived", "deleted", "drafts"].includes(initialSecondaryViewParam)
    ? initialSecondaryViewParam
    : initialDraftsOnly
      ? "drafts"
    : "";

  const [tabValue, setTabValue] = useState(initialTab);
  const [confirmSwipeAction, setConfirmSwipeAction] = useState(null);
  const [sentSponsoredOnly, setSentSponsoredOnly] = useState(false);
  const [searchValue, setSearchValue] = useState(initialSearch);
  // Debounced copy used for filtering so typing doesn't re-run filters on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    String(initialSearch || "").trim().toLowerCase()
  );
  const [accountType, setAccountType] = useState("");
  const [secondaryView, setSecondaryView] = useState(initialSecondaryView);
  const [messageViewMenuAnchor, setMessageViewMenuAnchor] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  // Received-only: read-state chip + sender filter. Sort applies across list tabs.
  const [readFilter, setReadFilter] = useState("all");
  const [fromSender, setFromSender] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [headerHeight, setHeaderHeight] = useState(124);
  const [actionError, setActionError] = useState("");
  const headerShellRef = useRef(null);
  const isBusinessAccount = String(accountType || "").trim().toLowerCase() === "business";
  const activeView = secondaryView || (tabValue === 0 ? "received" : tabValue === 1 ? "sent" : "memories");
  const showSentFilters = activeView === "sent" && isBusinessAccount;

  useLayoutEffect(() => {
    const headerEl = headerShellRef.current;
    if (!headerEl) return;

    const measure = () => {
      const nextHeight = Math.ceil(headerEl.getBoundingClientRect().height || 0);
      if (nextHeight > 0) setHeaderHeight(nextHeight);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => measure());
      observer.observe(headerEl);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [showSentFilters, secondaryView]);

  const syncMessageParams = useCallback((nextTab, nextSearch, nextSecondaryView) => {
    const nextParams = { tab: String(nextTab) };
    if (nextSearch.trim()) nextParams.q = nextSearch.trim();
    if (nextSecondaryView) nextParams.view = nextSecondaryView;
    setSearchParams(nextParams);
  }, [setSearchParams]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || "");
      setAuthResolved(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchValue.trim().toLowerCase());
    }, 200);
    return () => clearTimeout(id);
  }, [searchValue]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!uid) {
        if (active) setAccountType("");
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        if (!active) return;
        setAccountType(userSnap.exists() ? String(userSnap.data()?.accountType || "") : "");
      } catch (err) {
        console.warn("Failed to load message filters account type:", err);
        if (active) setAccountType("");
      }
    })();

    return () => {
      active = false;
    };
  }, [uid]);

  const { messages: receivedMessages, isLoading: isLoadingReceived } = useReceivedMessages(uid);
  const { messages: sentMessages, isLoading: isLoadingSent } = useSentMessages(uid);
  const {
    memory: memoryMessages,
    buried: buriedMessages,
    isLoading: isLoadingSelfPrivate,
  } = useSelfPrivateMessages(uid);
  const isLoadingMemories = isLoadingSelfPrivate;
  const isLoadingBuried = isLoadingSelfPrivate;
  const { messages: archivedMessages, isLoading: isLoadingArchived } = useArchivedMessages(uid);
  const { messages: deletedMessages, isLoading: isLoadingDeleted } = useDeletedMessages(uid);

  // human-friendly countdown renderer
  const countdownRenderer = useCallback(({ total, completed }) => {
    if (completed) {
      return <Typography  gutterBottom>This time capsule is now open!</Typography>;
    }
    const duration = formatDuration(total);
    return (
      <Typography   gutterBottom>
        {messageToBeOppendText+" "+duration}
      </Typography>
    );
  }, []);

  // archive a message
  const archiveMessage = async (capsuleId) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", capsuleId);
    await updateDoc(ref, { archivedBy: arrayUnion(uid) });
  };

  const unarchiveMessage = async (capsuleId) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", capsuleId);
    await updateDoc(ref, { archivedBy: arrayRemove(uid) });
  };

  const deleteMessageMedia = async (msg) => {
    const mediaItems = [
      ...(Array.isArray(msg?.media) ? msg.media : []),
      ...(Array.isArray(msg?.mediaFiles) ? msg.mediaFiles : []),
    ];

    const storagePaths = Array.from(
      new Set(
        mediaItems
          .filter((item) => item?.draftOwnedMedia === true)
          .map((item) => String(item?.storagePath || "").trim())
          .filter(Boolean)
      )
    );

    for (const path of storagePaths) {
      try {
        await deleteObject(storageRef(storage, path));
      } catch (err) {
        if (err?.code === "storage/object-not-found") continue;
        throw err;
      }
    }
  };

  // delete a message
  const deleteMessage = async (msg) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", msg.id);
    if (isDraftCapsule(msg)) {
      await deleteMessageMedia(msg);
      await deleteDoc(ref);
      return;
    }
    await updateDoc(ref, { deletedBy: arrayUnion(uid) });
  };

  const restoreDeletedMessage = async (capsuleId) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", capsuleId);
    await updateDoc(ref, { deletedBy: arrayRemove(uid) });
  };

  const handleChangeTab = useCallback((newVal) => {
    setTabValue(newVal);
    setSecondaryView("");
    syncMessageParams(newVal, searchValue, "");
  }, [searchValue, syncMessageParams]);
  //Get city and country name form location string
  function getCityAndCountry(location) {
    if (!location) return '';
  
    const parts = location.split(',').map(part => part.trim());
  
    if (parts.length < 2) return '';
  
    const city = parts[parts.length - 2];
    const country = parts[parts.length - 1];
  
    return `${city}, ${country}`;
  }

  const isDraftCapsule = useCallback((m) => isDraftCapsuleMessage(m), []);

  const isSponsoredCapsule = useCallback((m) => isSponsoredCapsuleMessage(m), []);

  const isSelfOnlyCapsule = useCallback((m) => {
    return isSelfOnlyPrivateCapsule(m, uid);
  }, [uid]);

  const {
    sentSponsoredCount,
    filteredDraftMessages,
    filteredSentMessages,
    filteredMemoryMessages,
  } = useMemo(() => {
    let draftCount = 0;
    let sponsoredCount = 0;
    const drafts = [];
    const filtered = [];

    buriedMessages.forEach((message) => {
      const isSponsoredMessage = isSponsoredCapsuleMessage(message);
      const isDraftMessage = isDraftCapsuleMessage(message);
      const isSelfOnlyMessage = isSelfOnlyCapsule(message);

      if (isSponsoredMessage) sponsoredCount += 1;
      if (isDraftMessage) return;

      if (sentSponsoredOnly && !isSponsoredMessage) return;

      if (isSelfOnlyMessage) {
        return;
      }

      filtered.push(message);
    });

    sentMessages.forEach((message) => {
      if (isDraftCapsuleMessage(message)) {
        draftCount += 1;
        drafts.push(message);
      }
    });

    return {
      sentDraftCount: draftCount,
      sentSponsoredCount: sponsoredCount,
      filteredDraftMessages: drafts,
      filteredSentMessages: filtered,
      filteredMemoryMessages: memoryMessages.filter((message) => isSelfOnlyCapsule(message)),
    };
  }, [
    buriedMessages,
    memoryMessages,
    sentMessages,
    sentSponsoredOnly,
    isSelfOnlyCapsule,
  ]);

  const handleSwipeActionRequest = useCallback((msg, actionType) => {
    setConfirmSwipeAction({ msg, type: actionType });
  }, []);

  const swipeDialogConfig = getSwipeDialogConfig(confirmSwipeAction);

  const handleConfirmSwipeAction = async () => {
    if (!confirmSwipeAction) return;

    const { msg, type } = confirmSwipeAction;
    setConfirmSwipeAction(null);

    try {
      if (type === "archive") {
        await archiveMessage(msg.id);
        return;
      }

      if (type === "unarchive") {
        await unarchiveMessage(msg.id);
        return;
      }

      if (type === "restore") {
        await restoreDeletedMessage(msg.id);
        return;
      }

      await deleteMessage(msg);
    } catch (err) {
      console.error("Failed to complete swipe action:", err);
      const actionLabel =
        type === "delete" ? (isDraftCapsule(msg) ? "discard" : "delete") : type;
      setActionError(`We couldn't ${actionLabel} that message. Please try again.`);
    }
  };

  const handleToggleSponsored = useCallback(() => {
    setSentSponsoredOnly((value) => {
      const nextValue = !value;
      syncMessageParams(tabValue, searchValue, secondaryView);
      return nextValue;
    });
  }, [searchValue, secondaryView, syncMessageParams, tabValue]);

  const handleSearchChange = useCallback((nextValue) => {
    setSearchValue(nextValue);
    syncMessageParams(tabValue, nextValue, secondaryView);
  }, [secondaryView, syncMessageParams, tabValue]);

  const handleOpenOverflowMenu = useCallback((event) => {
    setMessageViewMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseOverflowMenu = useCallback(() => {
    setMessageViewMenuAnchor(null);
  }, []);

  const handleSelectSecondaryView = useCallback((nextView) => {
    setSecondaryView(nextView);
    setMessageViewMenuAnchor(null);
    syncMessageParams(tabValue, searchValue, nextView);
  }, [searchValue, syncMessageParams, tabValue]);

  const handleClearSecondaryView = useCallback(() => {
    setSecondaryView("");
    syncMessageParams(tabValue, searchValue, "");
  }, [searchValue, syncMessageParams, tabValue]);

  const handleOpenSortMenu = useCallback((event) => {
    setSortMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseSortMenu = useCallback(() => {
    setSortMenuAnchor(null);
  }, []);

  const handleSelectSort = useCallback((nextSort) => {
    setSortBy(nextSort);
  }, []);

  const handleSelectFromSender = useCallback((nextSenderId) => {
    setFromSender(nextSenderId);
  }, []);

  const messageMatchesSearch = useCallback((message) => {
    const query = debouncedSearch;
    if (!query) return true;

    const fields = [
      message?.subject,
      message?.message,
      message?.senderDisplayName,
      message?.senderPublicHandle,
      message?.address,
      message?.callToActionText,
      Array.isArray(message?.hashtags) ? message.hashtags.join(" ") : "",
    ];
    return fields.some((field) => String(field || "").toLowerCase().includes(query));
  }, [debouncedSearch]);

  const isReceivedUnread = useCallback(
    (message) => !(Array.isArray(message?.readBy) ? message.readBy : []).includes(uid),
    [uid]
  );

  const unreadReceivedCount = useMemo(
    () => receivedMessages.reduce((count, m) => (isReceivedUnread(m) ? count + 1 : count), 0),
    [receivedMessages, isReceivedUnread]
  );

  // Distinct senders for the "From" filter in the sort menu.
  const receivedSenders = useMemo(() => {
    const byId = new Map();
    receivedMessages.forEach((message) => {
      const id = String(message?.senderId || "").trim();
      if (!id || byId.has(id)) return;
      const name =
        message.senderDisplayName ||
        (message.senderPublicHandle
          ? `@${String(message.senderPublicHandle).replace(/^@+/, "")}`
          : "Unknown");
      byId.set(id, { uid: id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [receivedMessages]);

  const draftsCount = filteredDraftMessages.length;
  const archivedCount = archivedMessages.length;
  const deletedCount = deletedMessages.length;
  const selectedFromSenderName = fromSender
    ? receivedSenders.find((sender) => sender.uid === fromSender)?.name || ""
    : "";

  const filteredReceivedView = useMemo(() => {
    let list = receivedMessages;
    if (readFilter === "unread") list = list.filter(isReceivedUnread);
    else if (readFilter === "read") list = list.filter((m) => !isReceivedUnread(m));
    if (fromSender) list = list.filter((m) => String(m?.senderId || "") === fromSender);
    return sortMessages(list.filter(messageMatchesSearch), sortBy);
  }, [receivedMessages, readFilter, fromSender, isReceivedUnread, messageMatchesSearch, sortBy]);
  const filteredSentView = useMemo(
    () => sortMessages(filteredSentMessages.filter(messageMatchesSearch), sortBy),
    [filteredSentMessages, messageMatchesSearch, sortBy]
  );
  const filteredMemoriesView = useMemo(
    () => sortMessages(filteredMemoryMessages.filter(messageMatchesSearch), sortBy),
    [filteredMemoryMessages, messageMatchesSearch, sortBy]
  );
  const filteredArchivedView = useMemo(
    () => sortMessages(archivedMessages.filter(messageMatchesSearch), sortBy),
    [archivedMessages, messageMatchesSearch, sortBy]
  );
  const filteredDeletedView = useMemo(
    () => sortMessages(deletedMessages.filter(messageMatchesSearch), sortBy),
    [deletedMessages, messageMatchesSearch, sortBy]
  );
  const filteredDraftsView = useMemo(
    () => sortMessages(filteredDraftMessages.filter(messageMatchesSearch), sortBy),
    [filteredDraftMessages, messageMatchesSearch, sortBy]
  );

  const activeViewLabel = useMemo(() => {
    if (activeView === "received") return "Received";
    if (activeView === "sent") return "Buried";
    if (activeView === "memories") return "Memories";
    if (activeView === "archived") return "Archived";
    if (activeView === "deleted") return "Deleted";
    if (activeView === "drafts") return "Drafts";
    return "Messages";
  }, [activeView]);

  const activeViewMessages = useMemo(() => {
    if (activeView === "received") return filteredReceivedView;
    if (activeView === "sent") return filteredSentView;
    if (activeView === "memories") return filteredMemoriesView;
    if (activeView === "archived") return filteredArchivedView;
    if (activeView === "deleted") return filteredDeletedView;
    if (activeView === "drafts") return filteredDraftsView;
    return [];
  }, [
    activeView,
    filteredArchivedView,
    filteredDeletedView,
    filteredDraftsView,
    filteredMemoriesView,
    filteredReceivedView,
    filteredSentView,
  ]);

  const activeViewIsLoading = useMemo(() => {
    if (!authResolved) return true;
    if (!uid) return false;
    if (activeView === "received") return isLoadingReceived;
    if (activeView === "sent") return isLoadingBuried;
    if (activeView === "memories") return isLoadingMemories;
    if (activeView === "archived") return isLoadingArchived;
    if (activeView === "deleted") return isLoadingDeleted;
    if (activeView === "drafts") return isLoadingSent;
    return false;
  }, [
    activeView,
    authResolved,
    isLoadingArchived,
    isLoadingBuried,
    isLoadingDeleted,
    isLoadingMemories,
    isLoadingReceived,
    isLoadingSent,
    uid,
  ]);

  // render each message
  const renderSwipeableItem = useCallback((msg, type) => {
    const now = new Date();
    const revealDate = msg.revealDate?.toDate
      ? msg.revealDate.toDate()
      : msg.revealDate
        ? new Date(msg.revealDate)
        : null;
    const isFuture = revealDate && revealDate > now;

    const sentDate = msg?.sentAt?.toDate ? msg.sentAt.toDate() : (msg?.sentAt ? new Date(msg.sentAt) : null);
    const sentAt = formatHumanDateTime(sentDate);
    const timeLabel = formatCompactTime(sentDate);
    const editedDate = msg?.lastEditedAt?.toDate
      ? msg.lastEditedAt.toDate()
      : (msg?.lastEditedAt
          ? new Date(msg.lastEditedAt)
          : (msg?.createdAt?.toDate ? msg.createdAt.toDate() : (msg?.createdAt ? new Date(msg.createdAt) : null)));
    const editedAt = formatHumanDateTime(editedDate);
    const isDraftMessage = type === "sent" && isDraftCapsule(msg);
    const isSponsoredSent = type === "sent" && isSponsoredCapsule(msg);
    const isSelfSent = type === "memories";
    const showAvatar = type === "received";
    const locationLabel = getCityAndCountry(msg.address || msg?.location?.address || "");
    const isUnreadMessage = type === "received" && !msg.readBy.includes(uid);
    const isUnreadComment =
      (type === "received" || type === "sent" || type === "memories") &&
      Array.isArray(msg.unreadCommentUserIds) &&
      msg.unreadCommentUserIds.includes(uid);
    const showUnreadDot = isUnreadMessage || isUnreadComment;
    const positiveSwipeAction =
      type === "archived"
        ? "unarchive"
        : type === "deleted"
          ? "restore"
          : "archive";
    const negativeSwipeAction =
      type === "deleted"
        ? null
        : "delete";
    const openMessage = () => {
      if (isDraftMessage) {
        navigate(
          `/timecapsule?lat=${msg?.location?.lat || 0}&lng=${msg?.location?.lng || 0}&address=${encodeURIComponent(
            msg?.address || ""
          )}`,
          {
            state: {
              capsule: {
                ...msg,
                id: msg.id,
                messageTitle: msg?.messageTitle || msg?.subject || "",
                messageBody: msg?.messageBody || msg?.message || "",
                location: { ...(msg.location || {}), address: msg.address },
              },
            },
          }
        );
        return;
      }
      navigate(`/capsule/${msg.id}`);
    };
    const openProfile = () => {
      if (msg.senderId) {
        navigate(getProfilePathByUid(msg.senderId));
      }
    };

    return (
        <MessageRowItem
          key={msg.id}
          msg={msg}
          type={type}
          onOpenMessage={openMessage}
          onOpenProfile={openProfile}
          onSwipeAction={handleSwipeActionRequest}
          countdownRenderer={countdownRenderer}
          revealDate={revealDate}
          isFuture={isFuture}
        sentAt={sentAt}
        editedAt={editedAt}
        timeLabel={timeLabel}
        isDraftMessage={isDraftMessage}
        isSponsoredSent={isSponsoredSent}
        isSelfSent={isSelfSent}
        showAvatar={showAvatar}
        locationLabel={locationLabel}
        showUnreadDot={showUnreadDot}
        positiveSwipeAction={positiveSwipeAction}
        negativeSwipeAction={negativeSwipeAction}
      />
    );
  }, [
    countdownRenderer,
    handleSwipeActionRequest,
    isDraftCapsule,
    isSponsoredCapsule,
    navigate,
    uid,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <Box
        ref={headerShellRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
        }}
      >
        <MessageHeader
          tabValue={tabValue}
          onChangeTab={handleChangeTab}
          showSentFilters={showSentFilters}
          sentSponsoredCount={sentSponsoredCount}
          sentSponsoredOnly={sentSponsoredOnly}
          isBusinessAccount={isBusinessAccount}
          onToggleSponsored={handleToggleSponsored}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          secondaryView={secondaryView}
          onClearSecondaryView={handleClearSecondaryView}
          onOpenFoldersMenu={handleOpenOverflowMenu}
          activeView={activeView}
          readFilter={readFilter}
          onSelectReadFilter={setReadFilter}
          unreadReceivedCount={unreadReceivedCount}
          sortShortLabel={getSortShortLabel(sortBy)}
          onOpenSortMenu={handleOpenSortMenu}
          fromSenderName={selectedFromSenderName}
          onClearFromSender={() => setFromSender("")}
        />
      </Box>

      <Menu
        anchorEl={messageViewMenuAnchor}
        open={Boolean(messageViewMenuAnchor)}
        onClose={handleCloseOverflowMenu}
        MenuListProps={{ dense: true }}
      >
        <ListSubheader sx={{ lineHeight: "32px", bgcolor: "transparent" }}>
          FOLDERS
        </ListSubheader>
        <MenuItem onClick={() => handleSelectSecondaryView("drafts")}>
          <ListItemIcon>
            <DriveFileRenameOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Drafts</ListItemText>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {draftsCount}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSelectSecondaryView("archived")}>
          <ListItemIcon>
            <Inventory2OutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archived</ListItemText>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {archivedCount}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSelectSecondaryView("deleted")}>
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Deleted</ListItemText>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {deletedCount}
          </Typography>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={handleCloseSortMenu}
        MenuListProps={{ dense: true }}
      >
        <ListSubheader sx={{ lineHeight: "32px", bgcolor: "transparent" }}>
          SORT BY
        </ListSubheader>
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={sortBy === option.value}
            onClick={() => {
              handleSelectSort(option.value);
              handleCloseSortMenu();
            }}
          >
            <ListItemIcon>
              {sortBy === option.value ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        {activeView === "received" && <Divider />}
        {activeView === "received" && (
          <ListSubheader sx={{ lineHeight: "32px", bgcolor: "transparent" }}>FROM</ListSubheader>
        )}
        {activeView === "received" && (
          <MenuItem
            selected={!fromSender}
            onClick={() => {
              handleSelectFromSender("");
              handleCloseSortMenu();
            }}
          >
            <ListItemIcon>{!fromSender ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>Everyone</ListItemText>
          </MenuItem>
        )}
        {activeView === "received" &&
          receivedSenders.map((sender) => (
            <MenuItem
              key={sender.uid}
              selected={fromSender === sender.uid}
              onClick={() => {
                handleSelectFromSender(sender.uid);
                handleCloseSortMenu();
              }}
            >
              <ListItemIcon>
                {fromSender === sender.uid ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{sender.name}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Message lists */}
      <Box
        sx={{
          position: "absolute",
          top: `${headerHeight}px`,
          right: 0,
          bottom: 0,
          left: 0,
          overflowY: "auto",
          overscrollBehaviorY: "contain",
          WebkitOverflowScrolling: "touch",
          backgroundColor: "background.default",
          px: 0.8,
          pb: 1,
          pt: 0.25,
          scrollPaddingBottom: "8px",
        }}
      >
        {activeViewIsLoading ? (
          <MessageListSkeleton />
        ) : activeViewMessages.length ? (
          <Box>
            {activeViewMessages.map((message) =>
              renderSwipeableItem(
                message,
                activeView === "drafts" ? "sent" : activeView
              )
            )}
          </Box>
        ) : (
          <MessageEmptyState
            hasSearch={Boolean(searchValue.trim())}
            activeViewLabel={activeViewLabel}
          />
        )}
      </Box>

      <Dialog
        open={Boolean(confirmSwipeAction)}
        onClose={() => setConfirmSwipeAction(null)}
      >
        <DialogTitle>{swipeDialogConfig.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{swipeDialogConfig.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSwipeAction(null)}>
            Cancel
          </Button>
          <Button
            color={swipeDialogConfig.confirmColor}
            onClick={handleConfirmSwipeAction}
            variant="contained"
          >
            {swipeDialogConfig.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(actionError)}
        autoHideDuration={5000}
        onClose={() => setActionError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setActionError("")}
          sx={{ width: "100%" }}
        >
          {actionError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessagesPage;
